import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Alert, Group, Loader, Paper, Stack, Text, Title } from '@mantine/core';
import { IconAlertTriangle, IconArrowBackUp } from '@tabler/icons-react';
import { ApiRequestError } from '../../../../api/client';
import { ConfirmButton } from '../../../../components/ConfirmButton';
import { DocStatusBadge } from '../../../../components/DocStatusBadge';
import { ClosePostPanel } from '../../../../features/production-packconv/components/ClosePostPanel';
import { RunConsumablesEditor } from '../../../../features/production-packconv/components/RunConsumablesEditor';
import { RunInputsEditor } from '../../../../features/production-packconv/components/RunInputsEditor';
import { RunOutputsEditor } from '../../../../features/production-packconv/components/RunOutputsEditor';
import { YieldVarianceBadge } from '../../../../features/production-packconv/components/YieldVarianceBadge';
import { useRecipe, useReverseAndCloneRun, useRun, useStage } from '../../../../features/production-packconv/api';
import { pendingRecipeConsumables } from '../../../../features/production-packconv/recipePreview';

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
        {label}
      </Text>
      <Text fw={700}>{value}</Text>
    </div>
  );
}

/** Run detail (BUILD_SPEC.md §8.3): the shared surface every production run —
 * shift or kiln batch — passes through for line entry, close, and post. A kiln
 * batch's "cages out: count, mass, MC" close step is just adding output lines
 * here, then using the same `ClosePostPanel` a shift's wizard uses. Posted runs
 * offer Reverse &amp; clone (plan.md QC fix #10). */
export const Route = createFileRoute('/_authenticated/production-packconv/runs/$runId')({
  component: RunDetailPage,
});

function RunDetailPage() {
  const { runId } = Route.useParams();
  const navigate = useNavigate();
  const runQuery = useRun(runId);
  const stageQuery = useStage(runQuery.data?.stageId);
  const recipeQuery = useRecipe(runQuery.data?.recipeId);
  const reverseClone = useReverseAndCloneRun(runId);

  if (runQuery.isLoading) {
    return (
      <Group>
        <Loader size="sm" />
        <Text c="dimmed">Loading run…</Text>
      </Group>
    );
  }

  if (runQuery.isError || !runQuery.data) {
    return (
      <Alert color="red" icon={<IconAlertTriangle size={18} />} title="Could not load run">
        {runQuery.error instanceof ApiRequestError ? runQuery.error.message : 'Run not found'}
      </Alert>
    );
  }

  const run = runQuery.data;
  const editable = run.status === 'OPEN' || run.status === 'CLOSED';
  const missingConsumables = pendingRecipeConsumables(recipeQuery.data, run.outputs, run.consumables).length;

  async function handleReverseClone() {
    const result = await reverseClone.mutateAsync();
    navigate({ to: '/production-packconv/runs/$runId', params: { runId: result.newRunId } });
  }

  return (
    <Stack gap="lg">
      <Group justify="space-between" align="flex-start">
        <div>
          <Title order={2}>{run.number}</Title>
          <Text c="dimmed">
            {stageQuery.data?.name ?? run.stageId} · {run.runMode}
            {run.shiftLabel ? ` · ${run.shiftLabel}` : ''}
          </Text>
        </div>
        <DocStatusBadge status={run.status} domain="productionRun" size="lg" />
      </Group>

      {run.status === 'POSTED' && (
        <Group>
          <ConfirmButton
            color="red"
            variant="light"
            leftSection={<IconArrowBackUp size={16} />}
            loading={reverseClone.isPending}
            label="Reverse & clone"
            message={`Reverse run ${run.number}? Its input/output/consumable stock movements and yield will be backed out by a reversing ledger entry, and a new DRAFT run will be created for you to redo.`}
            onConfirm={handleReverseClone}
          />
        </Group>
      )}
      {reverseClone.isError && (
        <Alert color="red" icon={<IconAlertTriangle size={16} />}>
          {reverseClone.error instanceof ApiRequestError ? reverseClone.error.message : 'Failed to reverse this run'}
        </Alert>
      )}

      {run.status === 'POSTED' && (
        <Paper withBorder radius="md" p="lg">
          <Stack gap="sm">
            <Text fw={600}>Yield vs recipe</Text>
            <Group grow>
              <Stat label="Actual yield" value={run.yieldDryPctActual !== null ? `${Number(run.yieldDryPctActual).toFixed(1)}%` : '—'} />
              <Stat label="Recipe expected" value={recipeQuery.data ? `${recipeQuery.data.expectedYieldDryPct}%` : '—'} />
              <div>
                <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                  Variance
                </Text>
                {run.yieldVariancePct !== null ? <YieldVarianceBadge variancePct={run.yieldVariancePct} /> : <Text fw={700}>—</Text>}
              </div>
            </Group>
          </Stack>
        </Paper>
      )}

      <RunInputsEditor runId={run.id} siteId={run.siteId} lines={run.inputs} editable={editable} />
      <RunOutputsEditor runId={run.id} siteId={run.siteId} lines={run.outputs} editable={editable} />
      <RunConsumablesEditor
        runId={run.id}
        siteId={run.siteId}
        lines={run.consumables}
        outputs={run.outputs}
        recipe={recipeQuery.data}
        editable={editable}
      />

      {editable && <ClosePostPanel run={run} recipe={recipeQuery.data} missingConsumableCount={missingConsumables} />}
    </Stack>
  );
}
